import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, BarChart3, Crown, RefreshCw, Swords, Users } from 'lucide-react';
import api from '../services/api';
import { useStore } from '../stores/useStore';
import BattleStatsSection from '../components/BattleStatsSection';

const TAB_BUTTONS = [
  { id: 'bot', label: 'Bot', icon: Swords },
  { id: 'users', label: 'Usuarios', icon: Users },
  { id: 'clans', label: 'Clanes', icon: Crown }
];

function formatNumber(value) {
  return new Intl.NumberFormat('es-ES').format(Number(value || 0));
}

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Sin fecha';
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: '2-digit' });
}

function TabButton({ active, icon: Icon, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-all ${
        active
          ? 'border-cyan-400/40 bg-cyan-500/15 text-cyan-100 shadow-[0_0_20px_rgba(34,211,238,0.12)]'
          : 'border-slate-700 bg-black/20 text-slate-300 hover:border-cyan-400/30 hover:text-white'
      }`}
    >
      <Icon size={16} />
      {children}
    </button>
  );
}

function RankingRow({ item, kind }) {
  const accent = kind === 'clan' ? 'from-fuchsia-400 to-cyan-400' : 'from-cyan-400 to-emerald-400';
  const badge = kind === 'clan' ? 'Clan' : 'Piloto';

  return (
    <div className="rounded-2xl border border-slate-700 bg-black/15 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className={`rounded-full bg-gradient-to-r ${accent} px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-slate-950`}>
              #{item.rank}
            </span>
            <span className="rounded-full border border-slate-700 px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] text-slate-400">
              {badge}
            </span>
          </div>
          <h3 className="mt-2 text-lg font-bold text-white">{item.username || item.name || 'Sin nombre'}</h3>
          <p className="text-xs text-slate-400">
            {kind === 'clan'
              ? `${item.members || 0} miembros · ${item.battles} combates · win rate ${item.winRate.toFixed(1)}%`
              : `${item.clanName || 'Sin clan'} · ${item.battles} combates · win rate ${item.winRate.toFixed(1)}%`}
          </p>
        </div>
        <div className="rounded-2xl border border-slate-700 bg-slate-950/50 px-4 py-3 text-right">
          <p className="text-[11px] uppercase tracking-[0.25em] text-slate-500">Score</p>
          <p className="mt-1 text-2xl font-black text-white">{formatNumber(item.score)}</p>
        </div>
      </div>

      <div className="mt-4 grid gap-2 md:grid-cols-4 text-xs">
        <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-3">
          <p className="text-slate-500">Victorias</p>
          <p className="mt-1 text-lg font-bold text-emerald-300">{formatNumber(item.victories)}</p>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-3">
          <p className="text-slate-500">XP</p>
          <p className="mt-1 text-lg font-bold text-cyan-200">{formatNumber(item.totalExperience)}</p>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-3">
          <p className="text-slate-500">Daño</p>
          <p className="mt-1 text-lg font-bold text-amber-200">{formatNumber(item.damageDealt)}</p>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-950/40 p-3">
          <p className="text-slate-500">Último combate</p>
          <p className="mt-1 text-sm font-bold text-white">{formatDate(item.lastFightAt)}</p>
        </div>
      </div>
    </div>
  );
}

function RankingBoard({ title, subtitle, items, kind, highlight }) {
  return (
    <section className="rounded-[2rem] border border-slate-700 bg-[#07111f] p-5 shadow-[0_0_50px_rgba(34,211,238,0.05)]">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-cyan-200">
            <BarChart3 size={13} /> {title}
          </div>
          <p className="mt-3 text-sm text-slate-400">{subtitle}</p>
        </div>
        {highlight && (
          <div className="rounded-2xl border border-slate-700 bg-black/20 px-4 py-3 text-right">
            <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Líder</p>
            <p className="mt-1 text-lg font-bold text-white">{highlight.username || highlight.name || 'Sin datos'}</p>
            <p className="text-xs text-slate-400">Score {formatNumber(highlight.score)} · Rank #{highlight.rank || 1}</p>
          </div>
        )}
      </div>

      <div className="mt-5 space-y-3">
        {items.length > 0 ? items.map((item) => (
          <RankingRow key={`${kind}-${item.id}`} item={item} kind={kind} />
        )) : (
          <div className="rounded-2xl border border-dashed border-slate-700 bg-black/20 p-4 text-sm text-slate-500">
            Todavía no hay datos suficientes para construir este ranking.
          </div>
        )}
      </div>
    </section>
  );
}

export default function BattleStats() {
  const navigate = useNavigate();
  const { robot } = useStore();

  const [activeTab, setActiveTab] = useState('bot');
  const [botStats, setBotStats] = useState(null);
  const [rankings, setRankings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!robot?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');
    try {
      const [botRes, rankingsRes] = await Promise.all([
        api.get('/combat/bot-stats'),
        api.get('/combat/rankings')
      ]);
      setBotStats(botRes.data || null);
      setRankings(rankingsRes.data || null);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las estadísticas de combate');
    } finally {
      setLoading(false);
    }
  }, [robot?.id]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      if (!robot?.id) {
        if (mounted) setLoading(false);
        return;
      }
      await loadData();
    })();

    return () => {
      mounted = false;
    };
  }, [loadData, robot?.id]);

  const userRankings = rankings?.users || [];
  const clanRankings = rankings?.clans || [];
  const highlights = rankings?.highlights || {};

  return (
    <div className="min-h-screen bg-[#04060d] text-slate-100">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="rounded-[2rem] border border-cyan-500/20 bg-[linear-gradient(180deg,rgba(7,17,31,0.98),rgba(4,8,16,0.98))] p-5 shadow-[0_0_50px_rgba(34,211,238,0.08)]">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.3em] text-cyan-200">
                <BarChart3 size={13} /> Página táctica
              </div>
              <h1 className="mt-3 text-3xl md:text-4xl font-black text-white">Estadísticas de batalla</h1>
              <p className="mt-2 max-w-3xl text-sm text-slate-400">
                Rendimiento del bot, ranking de usuarios y ranking de clanes en una sola vista ordenada por pestañas.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-700 bg-black/20 px-4 py-3 text-sm text-slate-100 hover:bg-black/35"
              >
                <ArrowLeft size={16} /> Volver al dashboard
              </button>
              <button
                type="button"
                onClick={loadData}
                className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100 hover:bg-cyan-500/20"
              >
                <RefreshCw size={16} /> Actualizar
              </button>
            </div>
          </div>

          <div className="mt-5 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
              <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Acceso</p>
              <p className="mt-2 text-lg font-bold text-white">{robot?.name || 'Sin bot activo'}</p>
              <p className="text-xs text-slate-400">Nv. {robot?.level || 1} · Energía táctica constante</p>
            </div>
            <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
              <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Cobertura</p>
              <p className="mt-2 text-lg font-bold text-white">Bot, usuarios y clanes</p>
              <p className="text-xs text-slate-400">Analítica consolidada y comparativa.</p>
            </div>
            <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
              <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Señal</p>
              <p className="mt-2 text-lg font-bold text-white">Líderes tácticos</p>
              <p className="text-xs text-slate-400">Vistas separadas para rankings globales.</p>
            </div>
          </div>
        </div>

        <div className="mt-5 rounded-3xl border border-slate-700 bg-[#07111f] p-3">
          <div className="flex flex-wrap gap-3">
            {TAB_BUTTONS.map((tab) => {
              const Icon = tab.icon;
              return (
                <TabButton
                  key={tab.id}
                  active={activeTab === tab.id}
                  icon={Icon}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </TabButton>
              );
            })}
          </div>
        </div>

        {error && (
          <div className="mt-5 rounded-2xl border border-rose-500/20 bg-rose-500/5 p-4 text-rose-100">
            {error}
          </div>
        )}

        {activeTab === 'bot' && (
          <div className="mt-5">
            <BattleStatsSection stats={botStats} loading={loading} error={error} onRefresh={loadData} />
          </div>
        )}

        {activeTab === 'users' && (
          <div className="mt-5 space-y-5">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Líder global</p>
                <p className="mt-2 text-lg font-bold text-white">{highlights.topUser?.username || 'Sin datos'}</p>
                <p className="text-xs text-slate-400">Score {formatNumber(highlights.topUser?.score || 0)}</p>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Tu posición</p>
                <p className="mt-2 text-lg font-bold text-white">#{highlights.viewerUser?.rank || '-'}</p>
                <p className="text-xs text-slate-400">{highlights.viewerUser?.username || robot?.name || 'Sin perfil'}</p>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Win rate líder</p>
                <p className="mt-2 text-lg font-bold text-white">{Number(highlights.topUser?.winRate || 0).toFixed(1)}%</p>
                <p className="text-xs text-slate-400">Combates: {formatNumber(highlights.topUser?.battles || 0)}</p>
              </div>
            </div>

            <RankingBoard
              title="Ranking de usuarios"
              subtitle="Clasificación global por rendimiento de combate, victorias y daño acumulado."
              items={userRankings}
              kind="user"
              highlight={highlights.topUser}
            />
          </div>
        )}

        {activeTab === 'clans' && (
          <div className="mt-5 space-y-5">
            <div className="grid gap-3 md:grid-cols-3">
              <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Clan líder</p>
                <p className="mt-2 text-lg font-bold text-white">{highlights.topClan?.name || 'Sin datos'}</p>
                <p className="text-xs text-slate-400">Score {formatNumber(highlights.topClan?.score || 0)}</p>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Tu clan</p>
                <p className="mt-2 text-lg font-bold text-white">#{highlights.viewerClan?.rank || '-'}</p>
                <p className="text-xs text-slate-400">{highlights.viewerClan?.name || 'Sin clan'}</p>
              </div>
              <div className="rounded-2xl border border-slate-700 bg-black/20 p-4">
                <p className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Miembros líderes</p>
                <p className="mt-2 text-lg font-bold text-white">{formatNumber(highlights.topClan?.members || 0)}</p>
                <p className="text-xs text-slate-400">Miembros activos en combate</p>
              </div>
            </div>

            <RankingBoard
              title="Ranking de clanes"
              subtitle="Clasificación agregada por clan principal, con suma de victorias, XP, daño y número de miembros."
              items={clanRankings}
              kind="clan"
              highlight={highlights.topClan}
            />
          </div>
        )}
      </div>
    </div>
  );
}
